import { PaymentInfoSelected, PaymentSectionProps } from '@msc-b2c/interfaces/types';
import { Text } from '../../text/Text';
import {
  formatPriceWithoutRounding,
  getCardCvvImage,
  getCreditCards,
  getCurrencySymbol,
  getOtherPaymentMethods,
  getPaymentButtons,
  getPriceCurrencyOrder,
  handleKeyDown,
  PaymentContext,
  replacePlaceholders,
  selectedPaymentInfo,
} from '@msc-b2c/shared-utils';
import styles from './paymentsection.module.scss';
import { RadioButton } from '../../radiobutton/RadioButton';
import { PaymentMethods } from '../PaymentMethods';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useContext, useState } from 'react';
import PaymentSubmit from '../../paymentbox/components/PaymentSubmit/PaymentSubmit';
import { Button } from '../../button/Button';
import clsx from 'clsx';

export function PaymentSection({
  type,
  sectionTitle,
  sectionDescription,
  sectionFreeCartDescription,
  cardPaymentDetailsLabel,
  cardNumberPlaceholder,
  cardExpirationDatePlaceholder,
  cardCvvPlaceholder,
  cardCvvDisclaimer,
  termsAndConditionSection,
  paymentButtonCTA,
  refundableDepositBannerDescription,
  refundableDepositBannerBackgroundColor,
  refundableDepositBannerTextColor,
  transactionId,
  totalAmount,
  mandatoryLabel,
  datatrans,
  isFreeCartEnabled = false,
  enableAvs,
  avsFormInputField,
}: Readonly<PaymentSectionProps>) {
  const currency = getCurrencySymbol();

  /* PAYMENT CONTEXT AWARE */
  const allowedCards = getCreditCards();
  const allowedButtons = getPaymentButtons();
  const allowedOther = getOtherPaymentMethods();
  // if allowedCards is not empty, set "card" as paymentMethod default, or if allowedButtons is not empty, set "button" as paymentMethod default
  const paymentMethodDefault =
    allowedCards.length > 0 ? 'card' : allowedButtons.length > 0 ? 'button' : 'other';
  const dispatch = useDispatch();
  const selectedPayment = useSelector((state) => state.payment.selectedPayment);
  const active = selectedPayment?.type === type;
  const onPaymentMethodChange = (type: string, price, paymentMethod) => {
    const paymentInfo: PaymentInfoSelected = {
      type,
      price,
      paymentMethod,
    };
    dispatch(selectedPaymentInfo(paymentInfo));
  };

  const [isFreeCartAgreementChecked, setIsFreeCartAgreementChecked] = useState(false);

  const isFreeCart = useCallback(() => {
    if (!totalAmount || !isFreeCartEnabled) return false;

    return Number(totalAmount) === 0;
  }, [totalAmount, isFreeCartEnabled]);

  const { onFinalizePayment } = useContext(PaymentContext);

  return (
    <div className={`${styles['cmp-paymentSection']} ${active ? styles.active : ''}`}>
      <div className={styles['cmp-paymentSection__header']}>
        <div className={styles['cmp-paymentSection__header-title']}>
          <div
            className={`${styles['cmp-paymentSection__header-titleContainer']} ${
              refundableDepositBannerDescription ? 'is-deposit' : ''
            }`}
          >
            <Text
              text={sectionTitle}
              styleOption={'body-big title'}
              tagHtml={'p'}
            />
            {refundableDepositBannerDescription && (
              <div
                className={styles['cmp-paymentSection__header-refundablebanner']}
                style={{
                  backgroundColor: refundableDepositBannerBackgroundColor ?? '',
                  color: refundableDepositBannerTextColor ?? 'inherit',
                }}
              >
                <Text
                  text={refundableDepositBannerDescription}
                  styleOption={'body-small'}
                />
              </div>
            )}
            <Text
              text={(isFreeCart() && sectionFreeCartDescription) || sectionDescription}
              tagHtml={'span'}
              styleOption={'cmp-paymentSection__header-subtitle'}
            />
          </div>

          <div
            onClick={() => onPaymentMethodChange(type, totalAmount, paymentMethodDefault)}
            onKeyDown={(event) =>
              handleKeyDown(event, onPaymentMethodChange, [type, totalAmount, paymentMethodDefault])
            }
            tabIndex={0}
            role="button"
            aria-label={sectionTitle}
            className={`${styles['priceBox']} ${styles.absolutePrice}`}
          >
            <span className={styles.price}>
              {replacePlaceholders(getPriceCurrencyOrder(), {
                currency: currency,
                price: formatPriceWithoutRounding(totalAmount),
              })}
            </span>
            <RadioButton
              id={`payment-section-radio-${type}`}
              name="payment-section-selection"
              value={type}
              checked={active}
              onChange={() => onPaymentMethodChange(type, totalAmount, paymentMethodDefault)}
              ariaLabel={sectionTitle}
              tabIndex={-1}
            />
          </div>
        </div>
      </div>

      {(isFreeCart() && (
        <div className={clsx(styles['cmp-paymentSection__card-content'], styles['-active'])}>
          <div className={styles['cmp-paymentCard']}>
            <PaymentSubmit
              id={`${type}-terms${cardNumberPlaceholder.replaceAll(/\s/g, '')}`}
              terms={termsAndConditionSection}
              onCheck={() => {
                setIsFreeCartAgreementChecked((prev) => !prev);
              }}
              checked={isFreeCartAgreementChecked}
            >
              <Button
                id={`form-submit-${type}`}
                buttonType={'submit'}
                text={paymentButtonCTA}
                accessibilityLabel={paymentButtonCTA}
                disabled={!isFreeCartAgreementChecked}
                styleOption={'cmp-button__primary -extended'}
                onClick={onFinalizePayment}
              />
            </PaymentSubmit>
          </div>
        </div>
      )) || (
        <div
          className={`${styles['cmp-paymentSection__card-content']} ${active ? '-active' : 'hide'}`}
        >
          <PaymentMethods
            datatransSecureFieldUrl={datatrans.secureFieldUrl}
            datatransPaymentButtonUrl={datatrans.paymentButtonUrl}
            datatransPayPalUrl={datatrans.payPalUrl}
            merchantIdBooking={datatrans.datatransCountryInfo.merchantId}
            campaignIdPaymentNotification={datatrans.datatransCountryInfo.campaignId}
            gpayMerchantId={datatrans.datatransCountryInfo.gpay}
            cardLabel={cardNumberPlaceholder}
            cvvLabel={cardCvvPlaceholder}
            detailsLabel={cardPaymentDetailsLabel}
            orderButtonLabel={paymentButtonCTA}
            orderButtonAriaLabel={paymentButtonCTA}
            type={type}
            mandatorylabel={mandatoryLabel}
            disclaimer={cardCvvDisclaimer}
            termsLabel={termsAndConditionSection}
            visaImg={allowedCards.find((card) => card.type === 'VIS')?.pathImage ?? ''}
            mastercardImg={allowedCards.find((card) => card.type === 'ECA')?.pathImage ?? ''}
            amexImg={allowedCards.find((card) => card.type === 'AMX')?.pathImage ?? ''}
            paypalImg={allowedOther.find((button) => button.type === 'PAP')?.pathImage ?? ''}
            googleImg={allowedButtons.find((button) => button.type === 'PAY')?.pathImage ?? ''}
            appleImg={allowedButtons.find((button) => button.type === 'APL')?.pathImage ?? ''}
            klarnaImg={''}
            dinersImg={getCreditCards().find((card) => card.type === 'DIN')?.pathImage ?? ''}
            cardImg={getCardCvvImage() ?? ''}
            expirationLabel={cardExpirationDatePlaceholder}
            transactionId={transactionId}
            price={totalAmount}
            enableAvs={enableAvs}
            avsFormInputField={avsFormInputField}
          />
        </div>
      )}
    </div>
  );
}
